import { useRef, useCallback } from "react";
import { cn } from "~/lib/utils";

interface UnitToggleProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: T[];
  /** Accessible label for the radiogroup */
  label: string;
  disabled?: boolean;
}

/**
 * Segmented control for toggling between unit options (km/mi, m/ft, etc.)
 * All options are tabbable for inline editor workflows.
 * Arrow keys also work as an enhancement.
 */
export function UnitToggle<T extends string>({ value, onChange, options, label, disabled }: UnitToggleProps<T>) {
  const groupRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      let nextIndex: number | null = null;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % options.length;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        nextIndex = (currentIndex - 1 + options.length) % options.length;
      }

      if (nextIndex !== null) {
        onChange(options[nextIndex]);
        const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
        buttons?.[nextIndex]?.focus();
      }
    },
    [options, onChange],
  );

  return (
    <div ref={groupRef} className="inline-flex rounded-md border border-border" role="radiogroup" aria-label={label}>
      {options.map((option, index) => {
        const isSelected = value === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(option)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn("px-2 py-1 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md", {
              "bg-primary text-primary-foreground": isSelected,
              "bg-background text-muted-foreground hover:text-foreground": !isSelected,
              "opacity-50 cursor-not-allowed": disabled,
            })}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}
